import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';

/**
 * Service to handle native functionality with Capacitor
 */
export class NativeService {
  
  /**
   * Check if the app is running on a native platform
   */
  static isNative() {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get the platform (ios, android, web)
   */
  static getPlatform() {
    return Capacitor.getPlatform();
  }

  /**
   * Hide the splash screen
   */
  static hideSplashScreen() {
    if (this.isNative()) {
      try {
        SplashScreen.hide();
      } catch (error) {
        console.error('Error hiding splash screen:', error);
      }
    }
  }

  /**
   * Get the app info
   */
  static getAppInfo() {
    return {
      name: 'OP Booking Admin',
      version: '1.0.0',
      platform: this.getPlatform()
    };
  }

  /**
   * Check if running on Android
   */
  static isAndroid() {
    return this.getPlatform() === 'android';
  }

  /**
   * Check if running on iOS
   */
  static isIOS() {
    return this.getPlatform() === 'ios';
  }

  /**
   * Initialize app lifecycle listeners
   */
  static initializeAppLifecycle(onAppStateChange) {
    if (!this.isNative()) return;

    try {
      // Listen for app state changes
      App.addListener('appStateChange', ({ isActive }) => {
        console.log('App state changed:', { isActive });
        onAppStateChange && onAppStateChange(isActive);
      });

      // Listen for app URL open (for deep linking)
      App.addListener('appUrlOpen', (data) => {
        console.log('App opened with URL:', data);
      });

      // Listen for app resume
      App.addListener('resume', () => {
        console.log('App resumed');
        onAppStateChange && onAppStateChange(true);
      });

      // Listen for app pause
      App.addListener('pause', () => {
        console.log('App paused');
        onAppStateChange && onAppStateChange(false);
      });

      console.log('App lifecycle listeners initialized');
    } catch (error) {
      console.error('Error initializing app lifecycle:', error);
    }
  }

  /**
   * Secure storage methods for authentication data
   */
  static async setSecureItem(key, value) {
    if (!this.isNative()) {
      // Fallback to localStorage for web
      localStorage.setItem(key, value);
      return;
    }

    try {
      await Preferences.set({
        key: key,
        value: typeof value === 'object' ? JSON.stringify(value) : value
      });
    } catch (error) {
      console.error('Error setting secure item:', error);
      // Fallback to localStorage
      localStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  }

  static async getSecureItem(key) {
    if (!this.isNative()) {
      // Fallback to localStorage for web
      return localStorage.getItem(key);
    }

    try {
      const result = await Preferences.get({ key: key });
      return result.value;
    } catch (error) {
      console.error('Error getting secure item:', error);
      // Fallback to localStorage
      return localStorage.getItem(key);
    }
  }

  static async removeSecureItem(key) {
    if (!this.isNative()) {
      // Fallback to localStorage for web
      localStorage.removeItem(key);
      return;
    }

    try {
      await Preferences.remove({ key: key });
    } catch (error) {
      console.error('Error removing secure item:', error);
      // Fallback to localStorage
      localStorage.removeItem(key);
    }
  }

  /**
   * Authentication persistence methods
   */
  static async saveAuthData(authData) {
    const { token, user, userRole, refreshToken } = authData;
    
    await this.setSecureItem('auth_token', token);
    await this.setSecureItem('user', user);
    await this.setSecureItem('userRole', userRole);
    if (refreshToken) {
      await this.setSecureItem('refresh_token', refreshToken);
    }
    await this.setSecureItem('loginTimestamp', Date.now().toString());
  }

  static async getAuthData() {
    const token = await this.getSecureItem('auth_token');
    const user = await this.getSecureItem('user');
    const userRole = await this.getSecureItem('userRole');
    const refreshToken = await this.getSecureItem('refresh_token');
    const loginTimestamp = await this.getSecureItem('loginTimestamp');

    return {
      token,
      user: user ? JSON.parse(user) : null,
      userRole,
      refreshToken,
      loginTimestamp
    };
  }

  static async clearAuthData() {
    await this.removeSecureItem('auth_token');
    await this.removeSecureItem('user');
    await this.removeSecureItem('userRole');
    await this.removeSecureItem('refresh_token');
    await this.removeSecureItem('loginTimestamp');
    
    // Also clear localStorage as fallback
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('loginTimestamp');
  }

  /**
   * Check if app is currently active
   */
  static async isAppActive() {
    if (!this.isNative()) return true;
    
    try {
      const state = await App.getState();
      return state.isActive;
    } catch (error) {
      console.error('Error getting app state:', error);
      return true; // Assume active if we can't determine
    }
  }

  /**
   * Get app state
   */
  static async getAppState() {
    if (!this.isNative()) return { isActive: true };
    
    try {
      return await App.getState();
    } catch (error) {
      console.error('Error getting app state:', error);
      return { isActive: true };
    }
  }
}

export default NativeService; 